import React from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  useSortable,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Stack,
  Group,
  TextInput,
  Button,
  ActionIcon,
  Text,
} from "@mantine/core";
import { IconGripVertical, IconTrash, IconPlus } from "@tabler/icons-react";
import { ServiceAdjustmentFormQuestion } from "../../types/serviceAdjustmentForms";
import DraggableAnswerOption from "./DraggableAnswerOption";
import styles from "./AdjustmentFormsTab.module.css";

interface DraggableQuestionProps {
  question: ServiceAdjustmentFormQuestion;
  index: number;
  onQuestionChange: (index: number, value: string) => void;
  onDeleteQuestion: (index: number) => void;
  onAddOption: (questionIndex: number) => void;
  onOptionChange: (
    questionIndex: number,
    optionIndex: number,
    field: string,
    value: any,
  ) => void;
  onDeleteOption: (questionIndex: number, optionIndex: number) => void;
  onReorderOptions: (
    questionIndex: number,
    oldIndex: number,
    newIndex: number,
  ) => void;
}

function DraggableQuestion({
  question,
  index,
  onQuestionChange,
  onDeleteQuestion,
  onAddOption,
  onOptionChange,
  onDeleteOption,
  onReorderOptions,
}: DraggableQuestionProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.question_id || `temp-q-${index}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleOptionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    // Find indices by option_id with stable index-based fallback
    const oldOptionIndex = question.options.findIndex(
      (opt, idx) => (opt.option_id || `temp-o-${idx}`) === activeId,
    );
    const newOptionIndex = question.options.findIndex(
      (opt, idx) => (opt.option_id || `temp-o-${idx}`) === overId,
    );

    if (oldOptionIndex !== -1 && newOptionIndex !== -1) {
      onReorderOptions(index, oldOptionIndex, newOptionIndex);
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${styles.questionContainer} ${isDragging ? styles.dragging : ""}`}
    >
      <Stack gap="md">
        <Group gap="xs" align="flex-start">
          <div {...attributes} {...listeners} className={styles.dragHandle}>
            <IconGripVertical size={20} />
          </div>
          <TextInput
            placeholder="Question"
            value={question.question}
            onChange={(e) => onQuestionChange(index, e.target.value)}
            style={{ flex: 1 }}
            required
          />
          <ActionIcon
            color="red"
            variant="subtle"
            onClick={() => onDeleteQuestion(index)}
            style={{ marginTop: "1.5rem" }}
            className={styles.deleteButton}
          >
            <IconTrash size={18} />
          </ActionIcon>
        </Group>

        <Stack gap="xs" ml="2rem">
          <Text size="sm" fw={500}>
            Answer Options
          </Text>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleOptionDragEnd}
          >
            <SortableContext
              items={question.options.map(
                (opt, idx) => opt.option_id || `temp-o-${idx}`,
              )}
              strategy={verticalListSortingStrategy}
            >
              <Stack gap="xs">
                {question.options.map((option, optionIndex) => (
                  <DraggableAnswerOption
                    key={option.option_id || `option-${index}-${optionIndex}`}
                    option={option}
                    questionIndex={index}
                    optionIndex={optionIndex}
                    onOptionChange={onOptionChange}
                    onDeleteOption={onDeleteOption}
                  />
                ))}
              </Stack>
            </SortableContext>
          </DndContext>
          <Button
            variant="light"
            size="xs"
            leftSection={<IconPlus size={14} />}
            onClick={() => onAddOption(index)}
          >
            Add Answer Option
          </Button>
        </Stack>
      </Stack>
    </div>
  );
}

export default DraggableQuestion;
